import React, { useMemo } from "react";
import DateFormatter from "../../../../app/formatters/DateFormatter";
import { getAddHrefUrl } from "../RpaUrlHelper";

/** @typedef {import('../../services/RpaPlanningService.js').ModifiedRpaResourceItem } ModifiedRpaResourceItem */
/** @typedef {import('../../services/RpaPlanningService.js').ModifiedRpaScheduleGridOfCurrentDayItem } ModifiedRpaScheduleGridOfCurrentDayItem */
/** @typedef {import('../../services/RpaPlanningService.js').RpaResourcesPerDayObj } RpaResourcesPerDayObj */

const REGROUPEMENTS = ["Matin", "Après-midi", "Soir"];

/**
 * @typedef {object} RpaReservationStepScheduleGridProps
 * @property {Array<RpaResourcesPerDayObj>} planningDays - Journées du planning
 * @property {Array<number>} selectedResourceIds - Ressources sélectionnées
 * @property {?string} selectedDateStr - Date sélectionnée
 * @property {boolean} transportAndParkingOption - Gestion des transports et stationnements
 * @property {boolean} [showResourceName] - Afficher le terrain sur chaque plage horaire
 */

/**
 * Étape de sélection du créneau
 * @param {RpaReservationStepScheduleGridProps} props
 */
export default function RpaReservationStepScheduleGrid({ planningDays, selectedResourceIds, selectedDateStr, transportAndParkingOption, showResourceName = false }) {
  const scheduleGridGroups = useMemo(() => {
    const day = planningDays.find(planningDay => planningDay.dayStr === selectedDateStr);
    const entries = (day?.rpaResources || [])
      .filter(resource => selectedResourceIds.includes(Number(resource.id)))
      .flatMap(resource => resource.scheduleGridsOfCurrentDay.filter(isScheduleGridVisible).map(rpaScheduleGrid => ({ rpaResource: resource, rpaScheduleGrid })))
      .sort((entryA, entryB) => entryA.rpaScheduleGrid.start_hour.localeCompare(entryB.rpaScheduleGrid.start_hour, undefined, { numeric: true }));

    return REGROUPEMENTS.map(regroupementStr => {
      const groupEntries = entries.filter(entry => entry.rpaScheduleGrid.regroupementStr === regroupementStr);

      return {
        regroupementStr,
        entries: groupEntries,
        nbAvailableScheduleGrids: groupEntries.filter(entry => isScheduleGridAvailable(entry.rpaResource, entry.rpaScheduleGrid)).length
      };
    }).filter(group => group.entries.length);
  }, [planningDays, selectedDateStr, selectedResourceIds]);

  if (!selectedResourceIds.length || !selectedDateStr) {
    return null;
  }

  return (
    <section>
      <div className="space--16"></div>
      <h3 className="title title--small title--no-margin">Créneau</h3>
      <div className="space--16"></div>
      {scheduleGridGroups.length ? (
        scheduleGridGroups.map(group => (
          <div key={group.regroupementStr}>
            <h4 className="title title--h5 title--no-margin title--flexline">
              {group.regroupementStr} <p className="paragraph paragraph--small-font-size paragraph--no-margin">{group.nbAvailableScheduleGrids} libre</p>
            </h4>
            <div className="space--8"></div>
            <div className="grid grid--has-8-child grid--has-4-child-mobile grid--8-gap no-mobile">
              {group.entries.map(({ rpaResource, rpaScheduleGrid }) =>
                isScheduleGridFull(rpaResource, rpaScheduleGrid) ? (
                  <button key={`${rpaResource.id}-${rpaScheduleGrid.id}`} type="button" className="btn btn--disable btn--two-labels" disabled>
                    <span className="btn__label--bold btn__label--line-through">{DateFormatter.formatHour(rpaScheduleGrid.start_hour, true, true)}</span>
                    <small className="btn__label--small">Complet</small>
                    {showResourceName ? <small className="btn__label--small">{rpaResource.name}</small> : null}
                  </button>
                ) : (
                  <a key={`${rpaResource.id}-${rpaScheduleGrid.id}`} data-loader="true" className="btn btn--main-color btn--two-labels" href={getAddHrefUrl("front", selectedDateStr, rpaResource, rpaScheduleGrid, transportAndParkingOption)}>
                    <span className="btn__label--bold">{DateFormatter.formatHour(rpaScheduleGrid.start_hour, true, true)}</span>
                    <small className="btn__label--small">{rpaScheduleGrid.durationStr}</small>
                    {showResourceName ? <small className="btn__label--small">{rpaResource.name}</small> : null}
                  </a>
                )
              )}
            </div>
            <div className="space--8"></div>
          </div>
        ))
      ) : (
        <p>Aucun créneau disponible pour cette date.</p>
      )}
    </section>
  );
}

/**
 * Retourne si une plage horaire future peut être affichée
 * TU TODO 🔨
 * @param {ModifiedRpaScheduleGridOfCurrentDayItem} rpaScheduleGrid - Plage horaire
 * @returns {boolean}
 */
export function isScheduleGridVisible(rpaScheduleGrid) {
  return !rpaScheduleGrid.closed && !rpaScheduleGrid.passed;
}

/**
 * Retourne si une plage horaire peut être réservée
 * TU TODO 🔨
 * @param {ModifiedRpaResourceItem} rpaResource - Ressource sélectionnée
 * @param {ModifiedRpaScheduleGridOfCurrentDayItem} rpaScheduleGrid - Plage horaire
 * @returns {boolean}
 */
export function isScheduleGridAvailable(rpaResource, rpaScheduleGrid) {
  return isScheduleGridVisible(rpaScheduleGrid) && !isScheduleGridFull(rpaResource, rpaScheduleGrid);
}

/**
 * Retourne si une plage horaire est complète
 * TU TODO 🔨
 * @param {ModifiedRpaResourceItem} rpaResource - Ressource sélectionnée
 * @param {ModifiedRpaScheduleGridOfCurrentDayItem} rpaScheduleGrid - Plage horaire
 * @returns {boolean}
 */
function isScheduleGridFull(rpaResource, rpaScheduleGrid) {
  const reservations = rpaScheduleGrid.rpa_reservations || [];

  return rpaResource.multi_resas ? reservations.length >= rpaResource.max_number_with_multi_resas : reservations.length > 0;
}
